import { useState, type FormEvent } from 'react';
import { Button, cx, ErrorBox, Input } from '../components/ui';
import { config } from '../config';
import { supabase } from '../lib/supabase';

// Sign in once per device; it then stays signed in until "Sign out". No two-factor
// (Brett's choice).
//
// Password is the main way in. Email is the fallback, as a typed code rather than a
// link: corporate email security (Microsoft 365 Safe Links) opens links to scan them,
// which uses up a one-time sign-in link before the person clicks it. Supabase's
// emails now go through Resend (custom SMTP, login@simplefeecre.com, 2026-10-22), ~30 per hour.

/** Supabase redirects back with #error=…&error_description=… when a link fails. */
function linkErrorFromUrl(): string | null {
  const hash = new URLSearchParams(window.location.hash.slice(1));
  if (!hash.get('error')) return null;
  history.replaceState(null, '', window.location.pathname);
  if (hash.get('error_code') === 'otp_expired') {
    return 'That sign-in link had already been used or expired (work email security often opens links before you do). Sign in with your password, or use the emailed code.';
  }
  return hash.get('error_description')?.replace(/\+/g, ' ') ?? 'That sign-in link didn’t work.';
}

function friendly(message: string): string {
  if (/invalid login credentials/i.test(message)) return 'Email or password is wrong. No password yet? Use “Email me a code” and set one in Settings.';
  if (/rate limit|security purposes/i.test(message)) return 'Too many sign-in emails in the last hour. Use your password, or wait a few minutes and send a new code.';
  if (/signups? not allowed|not allowed for otp/i.test(message)) return 'That email doesn’t have a login.';
  if (/expired|invalid/i.test(message)) return 'That code is wrong or has expired. Use the newest email, or send a new code.';
  return message;
}

export function Login() {
  const [mode, setMode] = useState<'password' | 'code'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(linkErrorFromUrl);

  async function run(fn: () => Promise<{ error: { message: string } | null }>, onOk?: () => void) {
    setBusy(true);
    setError(null);
    const { error } = await fn();
    setBusy(false);
    if (error) setError(friendly(error.message));
    else onOk?.();
    // On a successful sign-in the auth listener swaps this page for the app.
  }

  const signInWithPassword = (e: FormEvent) => {
    e.preventDefault();
    run(() => supabase.auth.signInWithPassword({ email: email.trim(), password }));
  };
  const sendCode = (e?: FormEvent) => {
    e?.preventDefault();
    run(
      () => supabase.auth.signInWithOtp({ email: email.trim(), options: { emailRedirectTo: config.appUrl, shouldCreateUser: false } }),
      () => setSent(true),
    );
  };
  const verifyCode = (e: FormEvent) => {
    e.preventDefault();
    run(() => supabase.auth.verifyOtp({ email: email.trim(), token: code.replace(/\s/g, ''), type: 'email' }));
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-7 shadow-sm">
        <div className="mb-5 text-2xl font-bold tracking-tight text-indigo-700">{config.appName}</div>
        <div className="mb-5 flex rounded-lg bg-slate-100 p-1 text-sm">
          {(['password', 'code'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => (setMode(m), setError(null))}
              className={cx('flex-1 rounded-md py-1.5 font-medium', mode === m ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500')}
            >
              {m === 'password' ? 'Password' : 'Email me a code'}
            </button>
          ))}
        </div>

        <label className="mb-4 block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Email</span>
          <Input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus disabled={mode === 'code' && sent} />
        </label>

        {mode === 'password' ? (
          <form onSubmit={signInWithPassword} className="space-y-4">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">Password</span>
              <Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
            </label>
            <ErrorBox error={error} />
            <Button variant="primary" className="w-full" busy={busy} type="submit" disabled={!email.trim()}>
              Sign in
            </Button>
          </form>
        ) : sent ? (
          <form onSubmit={verifyCode} className="space-y-4">
            <p className="text-sm text-slate-600">We emailed a sign-in code. Type it here.</p>
            <Input
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
              className="text-center text-lg tracking-[0.3em]"
              placeholder="123456"
            />
            <ErrorBox error={error} />
            <Button variant="primary" className="w-full" busy={busy} type="submit">
              Sign in
            </Button>
            <div className="flex justify-between text-xs">
              <button type="button" className="text-indigo-600 hover:underline" onClick={() => (setSent(false), setCode(''), setError(null))}>
                Different email
              </button>
              <button type="button" className="text-indigo-600 hover:underline" onClick={() => sendCode()}>
                Send a new code
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={sendCode} className="space-y-4">
            <ErrorBox error={error} />
            <Button variant="primary" className="w-full" busy={busy} type="submit" disabled={!email.trim()}>
              Email me a sign-in code
            </Button>
          </form>
        )}
        <p className="mt-5 text-center text-xs text-slate-400">This device stays signed in until you sign out.</p>
      </div>
    </div>
  );
}
